import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, Users, Briefcase, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_56a2be9b-13bb-4192-ac28-715d5016befb/artifacts/6hjkvofp_Foto%20WE%20ARE%20HIRING%20con%20logo.png';

const translations = {
  ca: {
    title: "BUSQUEM TALENT",
    subtitle: "Forma part de l'experiència UGOLF ARAVELL",
    intro: "Busquem persones dinàmiques, amb energia i orientació al client. Si t'agrada el tracte amb persones i vols treballar en un entorn únic entre golf i restauració… t'estem buscant.",
    functionsTitle: "Funcions",
    functions: [
      "Atenció al client al mostrador únic",
      "Gestió de reserves i vendes (golf i restauració)",
      "Ús de sistemes TPV"
    ],
    profileTitle: "Perfil",
    profile: [
      "Experiència en atenció al client i TPV",
      "Idiomes: català, francès o anglès",
      "Disponibilitat caps de setmana",
      "Actitud proactiva i orientació al client"
    ],
    offerTitle: "Oferim",
    offer: [
      "Entorn de treball dinàmic i de qualitat",
      "Contacte amb clientela internacional",
      "Integració en un equip professional"
    ],
    ctaQuestion: "T'interessa?",
    ctaText: "Envia el teu CV",
    ctaEmail: "direccio@aravellgolfclub.com",
    discoverUs: "Descobreix qui som"
  },
  es: {
    title: "BUSCAMOS TALENTO",
    subtitle: "Forma parte de la experiencia UGOLF ARAVELL",
    intro: "Buscamos personas dinámicas, con energía y orientación al cliente. Si te gusta el trato con personas y quieres trabajar en un entorno único entre golf y restauración… te estamos buscando.",
    functionsTitle: "Funciones",
    functions: [
      "Atención al cliente en el mostrador único",
      "Gestión de reservas y ventas (golf y restauración)",
      "Uso de sistemas TPV"
    ],
    profileTitle: "Perfil",
    profile: [
      "Experiencia en atención al cliente y TPV",
      "Idiomas: catalán, francés o inglés",
      "Disponibilidad fines de semana",
      "Actitud proactiva y orientación al cliente"
    ],
    offerTitle: "Ofrecemos",
    offer: [
      "Entorno de trabajo dinámico y de calidad",
      "Contacto con clientela internacional",
      "Integración en un equipo profesional"
    ],
    ctaQuestion: "¿Te interesa?",
    ctaText: "Envía tu CV",
    ctaEmail: "direccio@aravellgolfclub.com",
    discoverUs: "Descubre quiénes somos"
  },
  fr: {
    title: "NOUS RECRUTONS !",
    subtitle: "Rejoignez l'expérience UGOLF ARAVELL",
    intro: "Nous recherchons des personnes dynamiques, avec de l'énergie et un vrai sens du service client. Si vous aimez le contact avec les clients et souhaitez évoluer dans un environnement unique entre golf et restauration… ce poste est fait pour vous.",
    functionsTitle: "Vos missions",
    functions: [
      "Accueil et service client au point d'accueil unique",
      "Gestion des réservations et des ventes (golf et restauration)",
      "Utilisation des systèmes TPV"
    ],
    profileTitle: "Profil recherché",
    profile: [
      "Expérience en relation client et utilisation de TPV",
      "Langues : catalan, français ou anglais",
      "Disponibilité les week-ends",
      "Attitude proactive et orientation client"
    ],
    offerTitle: "Nous offrons",
    offer: [
      "Un environnement de travail dynamique et qualitatif",
      "Une clientèle internationale",
      "L'intégration dans une équipe professionnelle"
    ],
    ctaQuestion: "Intéressé(e) ?",
    ctaText: "Envoyez votre CV",
    ctaEmail: "direccio@aravellgolfclub.com",
    discoverUs: "Découvrez qui nous sommes"
  },
  en: {
    title: "WE ARE HIRING",
    subtitle: "Join the UGOLF ARAVELL experience",
    intro: "We are looking for dynamic, energetic people with a strong customer service mindset. If you enjoy working with clients and want to be part of a unique environment combining golf and hospitality… we'd love to meet you.",
    functionsTitle: "Your role",
    functions: [
      "Customer service at the main front desk",
      "Managing bookings and sales (golf and restaurant)",
      "Operating POS systems"
    ],
    profileTitle: "Your profile",
    profile: [
      "Experience in customer service and POS systems",
      "Languages: Catalan, French or English",
      "Weekend availability",
      "Proactive attitude and customer-oriented mindset"
    ],
    offerTitle: "What we offer",
    offer: [
      "A dynamic and high-quality working environment",
      "Direct contact with international clients",
      "Integration into a professional team"
    ],
    ctaQuestion: "Interested?",
    ctaText: "Send your CV",
    ctaEmail: "direccio@aravellgolfclub.com",
    discoverUs: "Discover who we are"
  }
};

const CareersPage = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.ca;

  const mailtoLink = `mailto:${t.ctaEmail}?subject=${encodeURIComponent('RRHH Sel·lecció Atenció Client')}`;

  return (
    <main data-testid="careers-page" className="bg-white">
      <SEO title={PAGE_SEO.careers.title} description={PAGE_SEO.careers.description} />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="UGOLF ARAVELL Team"
            className="w-full h-full object-contain object-center bg-[#1A4D2E]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </div>
        
        {/* Yellow accent - top left */}
        <div className="absolute top-0 left-0 w-24 h-24 bg-[#D4AF37]" />
        
        <div className="relative h-full min-h-[70vh] flex items-end">
          <div className="container-custom pb-8 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              {/* Subtitle - overlay text without background at bottom of photo */}
              <p className="text-xl md:text-2xl lg:text-3xl text-white font-light drop-shadow-lg tracking-wide">
                {t.subtitle}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Title Section - Green header at start of white section */}
      <section className="bg-white pt-12 pb-8">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center"
          >
            <div className="inline-block bg-[#1A4D2E] px-10 py-5">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white tracking-wide">
                {t.title}
              </h1>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">
            
            {/* Left Column - Intro & CTA */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="sticky top-24"
              >
                <p className="text-lg text-[#666666] leading-relaxed mb-8">
                  {t.intro}
                </p>
                
                {/* CTA Box */}
                <div className="bg-[#1A4D2E] p-8 text-white">
                  <p className="text-xl font-medium mb-4">{t.ctaQuestion}</p>
                  <a
                    href={mailtoLink}
                    className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#C4A030] text-white px-6 py-4 font-semibold tracking-wide transition-colors w-full justify-center"
                    data-testid="careers-cta-btn"
                  >
                    <Mail className="w-5 h-5" />
                    {t.ctaText}
                  </a>
                  <p className="text-sm text-white/80 mt-4 text-center">
                    {t.ctaEmail}
                  </p>
                </div>

                {/* Discover Us Link */}
                <Link
                  to="/"
                  className="mt-6 inline-flex items-center gap-2 text-[#1A4D2E] hover:text-[#D4AF37] font-medium transition-colors"
                >
                  <span>{t.discoverUs}</span>
                  <span>→</span>
                </Link>
              </motion.div>
            </div>

            {/* Right Column - Details */}
            <div className="lg:col-span-2 space-y-12">
              {/* Functions */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#1A4D2E] flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-display font-semibold text-[#1A4D2E]">
                    {t.functionsTitle}
                  </h2>
                </div>
                <ul className="space-y-3 ml-4">
                  {t.functions.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-[#666666]">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Profile */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#1A4D2E] flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-display font-semibold text-[#1A4D2E]">
                    {t.profileTitle}
                  </h2>
                </div>
                <ul className="space-y-3 ml-4">
                  {t.profile.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-[#666666]">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* What we offer */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-display font-semibold text-[#1A4D2E]">
                    {t.offerTitle}
                  </h2>
                </div>
                <ul className="space-y-3 ml-4">
                  {t.offer.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-[#666666]">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Yellow accent bar */}
              <div className="flex items-center gap-4 pt-8">
                <div className="h-1 w-24 bg-[#D4AF37]" />
                <div className="h-1 flex-1 bg-[#E5E5E5]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-display font-semibold text-[#1A4D2E] mb-6">
              {t.ctaQuestion}
            </h2>
            <a
              href={mailtoLink}
              className="inline-flex items-center gap-3 bg-[#1A4D2E] hover:bg-[#2A5D3E] text-white px-10 py-5 text-lg font-semibold tracking-wide transition-colors"
              data-testid="careers-cta-bottom-btn"
            >
              <Mail className="w-6 h-6" />
              {t.ctaText}
            </a>
            <p className="text-[#888888] mt-4">
              {t.ctaEmail}
            </p>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default CareersPage;
